import { useQueryClient } from '@tanstack/react-query'
import { FilterIdentifier, getSavedViewIdentifier } from 'models/saved_view'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { FilterStore } from '../FilterStore'
import { SavedFilterFormData, SavedFilterModal } from './SavedFilterModal'

interface CreateSavedFilterModalProps {
  show: boolean
  handleClose: () => void
  identifier: FilterIdentifier
  filterStore: FilterStore
  onFilterCreated?: () => void
}

export function CreateSavedFilterModal({
  show,
  handleClose,
  identifier,
  filterStore,
  onFilterCreated,
}: CreateSavedFilterModalProps) {
  const api = useApiService()
  const queryClient = useQueryClient()

  const { valueFilters, rangeFilters, zipCodeFilter, search, criteria } =
    filterStore()

  const handleSubmitSuccess = () => {
    toast.success('Filter saved!')
    onFilterCreated?.()
  }

  // Persist + invalidate against the base table identifier so a filter saved
  // on one campaign is reusable across every campaign of the same type
  // (FIR-7494). Non-campaign modifiers are left intact by the helper.
  const savedViewIdentifier = getSavedViewIdentifier(identifier)

  const createFilter = async (values: SavedFilterFormData) => {
    const filters = {
      valueFilters,
      rangeFilters,
      zipCodeFilter,
      search,
      criteria,
    }

    await api.saveFilters({
      identifier: savedViewIdentifier,
      name: values.name,
      shared: values.shared,
      payload: filters,
    })

    await queryClient.invalidateQueries({
      queryKey: ['saved_filters', savedViewIdentifier],
    })

    await queryClient.invalidateQueries({
      queryKey: ['saved-views/'],
    })
  }

  return (
    <SavedFilterModal
      show={show}
      handleClose={handleClose}
      onSubmitSuccess={handleSubmitSuccess}
      confirmButtonText="Save"
      submitApiAction={createFilter}
      title="Save Filter"
      description="Create a new filter to save this view, which includes both filters and any search terms."
    />
  )
}
